import React, { useState } from "react";
import { Download, FileText, FileSpreadsheet, Loader2 } from "lucide-react";
import { useAppState } from "../../hooks/useAppState";
import { esportaPerCommercialista } from "../../utils/exportCommercialista";

const currentYear = new Date().getFullYear();

export default function ExportCommercialistaCard({ darkMode }) {
  const { invoices, clients, fiscalSettings } = useAppState();

  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [isExporting, setIsExporting] = useState(false);

  const years = [currentYear, currentYear - 1, currentYear - 2];

  // Conteggi per anno selezionato
  const fattureAnno = invoices.filter((inv) => {
    if (!inv.issueDate) return false;
    return new Date(inv.issueDate).getFullYear() === selectedYear;
  });
  const numFatture = fattureAnno.length;
  const numClienti = new Set(fattureAnno.map((i) => i.clientName).filter(Boolean)).size;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      await esportaPerCommercialista({
        anno: selectedYear,
        invoices,
        clients,
        fiscalSettings,
      });
    } catch (err) {
      console.error("Errore export:", err);
      alert("Errore durante l'export. Riprova.");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <div className="space-y-4">
      {/* Selettore anno */}
      <div className="flex items-center gap-3">
        <label className={`text-sm font-medium ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
          Anno:
        </label>
        <select
          value={selectedYear}
          onChange={(e) => setSelectedYear(Number(e.target.value))}
          className={`px-3 py-2 rounded-lg border text-sm ${
            darkMode
              ? 'border-neutral-700 bg-neutral-800 text-white'
              : 'border-neutral-200 bg-white text-neutral-900'
          }`}
        >
          {years.map((y) => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
      </div>

      {/* Info rapido */}
      <div className={`flex flex-wrap gap-4 text-sm ${darkMode ? 'text-neutral-400' : 'text-neutral-600'}`}>
        <span>{numFatture} fatture</span>
        <span>{numClienti} clienti</span>
      </div>

      {/* File inclusi */}
      <div className={`rounded-xl p-4 ${darkMode ? 'bg-neutral-800' : 'bg-neutral-50'}`}>
        <p className={`text-xs font-semibold uppercase tracking-wide mb-3 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
          File inclusi nel pacchetto
        </p>
        <ul className="space-y-2">
          {[
            { icon: FileText, name: `riepilogo_fiscale_${selectedYear}.pdf`, desc: "Riepilogo dati fiscali" },
            { icon: FileSpreadsheet, name: `fatture_${selectedYear}.csv`, desc: "Elenco fatture" },
            { icon: FileSpreadsheet, name: `bollo_${selectedYear}.csv`, desc: "Fatture con bollo" },
            { icon: FileSpreadsheet, name: `clienti.csv`, desc: "Anagrafica clienti" },
            { icon: FileText, name: `LEGGIMI.txt`, desc: "Istruzioni" },
          ].map(({ icon: Icon, name, desc }) => (
            <li key={name} className="flex items-center gap-3">
              <Icon className={`w-4 h-4 shrink-0 ${darkMode ? 'text-neutral-500' : 'text-neutral-400'}`} />
              <div>
                <span className={`text-sm font-medium ${darkMode ? 'text-neutral-300' : 'text-neutral-700'}`}>{name}</span>
                <span className={`text-xs ml-2 ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>{desc}</span>
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Bottone download */}
      <button
        onClick={handleExport}
        disabled={isExporting || numFatture === 0}
        className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-medium transition-all ${
          isExporting || numFatture === 0
            ? darkMode
              ? 'bg-neutral-800 text-neutral-600 cursor-not-allowed'
              : 'bg-neutral-200 text-neutral-400 cursor-not-allowed'
            : 'bg-forfy-500 text-white hover:bg-forfy-600'
        }`}
      >
        {isExporting ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Generazione in corso...
          </>
        ) : (
          <>
            <Download className="w-4 h-4" />
            Scarica pacchetto ZIP
          </>
        )}
      </button>

      {numFatture === 0 && (
        <p className={`text-xs ${darkMode ? 'text-neutral-500' : 'text-neutral-500'}`}>
          Nessuna fattura per il {selectedYear}. Seleziona un anno con fatture per esportare.
        </p>
      )}
    </div>
  );
}
